import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { X, Cookie } from "lucide-react";
import { hasConsent, saveConsentPreferences } from "@/lib/consent";

export const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    // Show banner if user hasn't made a choice
    if (!hasConsent()) {
      setIsVisible(true);
    }
  }, []);

  const handleAcceptAll = () => {
    saveConsentPreferences({
      necessary: true,
      analytics: true,
      marketing: true,
    });
    setIsVisible(false);
  };

  const handleRejectAll = () => {
    saveConsentPreferences({
      necessary: true,
      analytics: false,
      marketing: false,
    });
    setIsVisible(false);
  };

  const handleSavePreferences = () => {
    saveConsentPreferences(preferences);
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center p-4 bg-black/50 backdrop-blur-sm">
      <Card className="w-full max-w-2xl p-6 shadow-elegant animate-in slide-in-from-bottom-5">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Cookie className="w-6 h-6 text-primary" />
            </div>
            <h2 className="text-2xl font-bold">Cookie Preferences</h2>
          </div>
        </div>

        {!showDetails ? (
          <>
            <p className="text-muted-foreground mb-6">
              We use cookies to enhance your experience, analyze site traffic, and provide personalized content. 
              You can choose which cookies to accept. For more information, read our{" "}
              <a href="/privacy" className="text-primary hover:underline">Privacy Policy</a>.
            </p>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button onClick={handleAcceptAll} variant="default" className="flex-1">
                Accept All Cookies
              </Button>
              <Button onClick={handleRejectAll} variant="outline" className="flex-1">
                Reject Non-Essential
              </Button>
              <Button onClick={() => setShowDetails(true)} variant="ghost" className="flex-1">
                Customize
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="space-y-4 mb-6">
              <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/30">
                <div className="flex-1">
                  <Label htmlFor="necessary" className="text-base font-semibold cursor-pointer">
                    Necessary Cookies
                  </Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    Required for the website to function properly. Cannot be disabled.
                  </p>
                </div>
                <Switch id="necessary" checked={true} disabled />
              </div>

              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex-1">
                  <Label htmlFor="analytics" className="text-base font-semibold cursor-pointer">
                    Analytics Cookies
                  </Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    Help us understand how visitors use our website through Google Analytics.
                  </p>
                </div>
                <Switch 
                  id="analytics" 
                  checked={preferences.analytics}
                  onCheckedChange={(checked) => setPreferences({...preferences, analytics: checked})}
                />
              </div>

              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex-1">
                  <Label htmlFor="marketing" className="text-base font-semibold cursor-pointer">
                    Marketing Cookies
                  </Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    Used to track visitors across websites for marketing purposes. (Not currently used)
                  </p>
                </div>
                <Switch 
                  id="marketing" 
                  checked={preferences.marketing}
                  onCheckedChange={(checked) => setPreferences({...preferences, marketing: checked})}
                />
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button onClick={handleSavePreferences} variant="default" className="flex-1">
                Save Preferences
              </Button>
              <Button onClick={() => setShowDetails(false)} variant="outline" className="flex-1">
                Back
              </Button>
            </div>
          </>
        )}
      </Card>
    </div>
  );
};
